import { c as _c } from "react/compiler-runtime";
import { type ChartConfig } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared-components/shadcn/chart";
import { Bar, CartesianGrid, XAxis, YAxis, Legend, BarChart, Tooltip } from "recharts";
import { COLORS } from "@shared-components/core-metrics/metric-utils";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";
import { pluginsMetadata } from "@chat-ai/_database/models/plugins-metadata";
import { type PluginTypeEnumTypes } from "@chat-ai/_database/enums";
type PluginsUsageProps = {
  startDate: Date;
  endDate: Date;
};
const chartConfig: ChartConfig = {
  count: {
    label: "Plugins usage"
  }
};
const secondChartConfig: ChartConfig = {
  count: {
    label: "Plugin categories"
  }
};
const pluginCategorizedMappedLabels = {
  noPlugins: "0 plugins",
  onePlugin: "1 plugin",
  twoPlugins: "2 plugins",
  multiplePlugins: "3+ plugins"
} as const;
const pluginLabel = (type: PluginTypeEnumTypes) => {
  const label = pluginsMetadata(type).name;
  return label.split(" ").join("\n");
};
const pluginCategorizedLabel = (value: string) => {
  return pluginCategorizedMappedLabels[value as keyof typeof pluginCategorizedMappedLabels] || value;
};
export const PluginsUsage = props => {
  const $ = _c(67);
  const {
    parentDepartmentId
  } = useDepartmentStackContext();
  let t0;
  if ($[0] !== parentDepartmentId || $[1] !== props.endDate || $[2] !== props.startDate) {
    t0 = {
      dateStart: props.startDate,
      dateEnd: props.endDate,
      initialDepartmentId: parentDepartmentId
    };
    $[0] = parentDepartmentId;
    $[1] = props.endDate;
    $[2] = props.startDate;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    data
  } = api.coreMetrics.pluginsUsageQuery.useQuery(t0);
  let t1;
  if ($[4] !== data?.pluginSpecific.overall) {
    t1 = data?.pluginSpecific.overall || [];
    $[4] = data?.pluginSpecific.overall;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = t1.map(_temp);
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const chartData = t2;
  let t3;
  if ($[8] !== data?.pluginSpecific.byDepartment) {
    t3 = data?.pluginSpecific.byDepartment || [];
    $[8] = data?.pluginSpecific.byDepartment;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const chartDataByDepartment = t3;
  let t4;
  if ($[10] !== data?.categorizedChats.overall) {
    t4 = data?.categorizedChats.overall || [];
    $[10] = data?.categorizedChats.overall;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = t4.map(_temp2);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const categorizedChatsData = t5;
  let t6;
  if ($[14] !== data?.categorizedChats.byDepartment) {
    t6 = data?.categorizedChats.byDepartment || [];
    $[14] = data?.categorizedChats.byDepartment;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = t6.map(_temp3);
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const categorizedChatsDataByDepartment = t7;
  let t8;
  if ($[18] !== data?.departments) {
    t8 = data?.departments || [];
    $[18] = data?.departments;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const departments = t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h2 className="mb-4">{chartConfig.count?.label}</h2>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  let t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CartesianGrid vertical={false} />;
    t11 = <XAxis dataKey="pluginType" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={_temp4} />;
    t12 = <YAxis yAxisId="count" dataKey="count" interval={1} orientation="left" />;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChartTooltip content={<ChartTooltipContent className="w-[150px]" nameKey="pluginType" labelFormatter={_temp5} />} />;
    t14 = <Legend />;
    t15 = <Bar yAxisId="count" dataKey="count" className="fill-ter-blue-500" fill="#006e96" />;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
  }
  let t16;
  if ($[27] !== chartData) {
    t16 = <ChartContainer config={chartConfig} className="aspect-auto h-[150px] w-full"><BarChart accessibilityLayer={true} data={chartData}>{t10}{t11}{t12}{t13}{t14}{t15}</BarChart></ChartContainer>;
    $[27] = chartData;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h2 className="mb-4 mt-8">{secondChartConfig.count?.label}</h2>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  let t19;
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <CartesianGrid vertical={false} />;
    t19 = <XAxis dataKey="category" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={_temp6} />;
    t20 = <YAxis yAxisId="count" dataKey="count" interval={1} orientation="left" />;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t18 = $[30];
    t19 = $[31];
    t20 = $[32];
  }
  let t21;
  let t22;
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ChartTooltip content={<ChartTooltipContent className="w-[150px]" nameKey="category" labelFormatter={_temp7} />} />;
    t22 = <Legend />;
    t23 = <Bar yAxisId="count" dataKey="count" className="fill-ter-blue-500" fill="#006e96" />;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
  } else {
    t21 = $[33];
    t22 = $[34];
    t23 = $[35];
  }
  let t24;
  if ($[36] !== categorizedChatsData) {
    t24 = <ChartContainer config={secondChartConfig} className="aspect-auto h-[150px] w-full"><BarChart accessibilityLayer={true} data={categorizedChatsData}>{t18}{t19}{t20}{t21}{t22}{t23}</BarChart></ChartContainer>;
    $[36] = categorizedChatsData;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="flex row gap-3 items-center mt-3"><h2 className="mb-4">{"Departments categorization"}</h2></div>;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <h2 className="mb-4">{chartConfig.count?.label}</h2>;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  let t28;
  let t29;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <CartesianGrid />;
    t28 = <XAxis dataKey="pluginType" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={_temp8} />;
    t29 = <YAxis yAxisId="count" interval={1} orientation="left" />;
    $[40] = t27;
    $[41] = t28;
    $[42] = t29;
  } else {
    t27 = $[40];
    t28 = $[41];
    t29 = $[42];
  }
  let t30;
  let t31;
  let t32;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ChartTooltip wrapperStyle={{
      zIndex: 1
    }} content={<ChartTooltipContent className="max-w-[350px]" itemsContainerClassName="grid grid-cols-3 gap-2" nameKey="departmentCode" labelFormatter={_temp9} />} />;
    t31 = <Tooltip />;
    t32 = <Legend />;
    $[43] = t30;
    $[44] = t31;
    $[45] = t32;
  } else {
    t30 = $[43];
    t31 = $[44];
    t32 = $[45];
  }
  let t33;
  if ($[46] !== departments) {
    t33 = departments.map(_temp10);
    $[46] = departments;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  let t34;
  if ($[48] !== chartDataByDepartment || $[49] !== t33) {
    t34 = <ChartContainer config={chartConfig} className="aspect-auto h-[150px] w-full"><BarChart accessibilityLayer={true} data={chartDataByDepartment}>{t27}{t28}{t29}{t30}{t31}{t32}{t33}</BarChart></ChartContainer>;
    $[48] = chartDataByDepartment;
    $[49] = t33;
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  let t35;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <h2 className="mb-4 mt-8">{secondChartConfig.count?.label}</h2>;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  let t37;
  let t38;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <CartesianGrid />;
    t37 = <XAxis dataKey="category" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={_temp11} />;
    t38 = <YAxis yAxisId="count" interval={1} orientation="left" />;
    $[52] = t36;
    $[53] = t37;
    $[54] = t38;
  } else {
    t36 = $[52];
    t37 = $[53];
    t38 = $[54];
  }
  let t39;
  let t40;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ChartTooltip wrapperStyle={{
      zIndex: 1
    }} content={<ChartTooltipContent className="max-w-[350px]" itemsContainerClassName="grid grid-cols-3 gap-2" nameKey="category" labelFormatter={_temp12} />} />;
    t40 = <Legend />;
    $[55] = t39;
    $[56] = t40;
  } else {
    t39 = $[55];
    t40 = $[56];
  }
  let t41;
  if ($[57] !== departments) {
    t41 = departments.map(_temp13);
    $[57] = departments;
    $[58] = t41;
  } else {
    t41 = $[58];
  }
  let t42;
  if ($[59] !== categorizedChatsDataByDepartment || $[60] !== t41) {
    t42 = <ChartContainer config={secondChartConfig} className="aspect-auto h-[150px] w-full"><BarChart accessibilityLayer={true} data={categorizedChatsDataByDepartment}>{t36}{t37}{t38}{t39}{t40}{t41}</BarChart></ChartContainer>;
    $[59] = categorizedChatsDataByDepartment;
    $[60] = t41;
    $[61] = t42;
  } else {
    t42 = $[61];
  }
  let t43;
  if ($[62] !== t16 || $[63] !== t24 || $[64] !== t34 || $[65] !== t42) {
    t43 = <div>{t9}{t16}{t17}{t24}{t25}{t26}{t34}{t35}{t42}</div>;
    $[62] = t16;
    $[63] = t24;
    $[64] = t34;
    $[65] = t42;
    $[66] = t43;
  } else {
    t43 = $[66];
  }
  return t43;
};
function _temp(d) {
  return {
    ...d,
    count: Number(d.count)
  };
}
function _temp2(d_0) {
  return {
    ...d_0,
    count: Number(d_0.count)
  };
}
function _temp3(d_1) {
  return {
    ...d_1,
    count: Number(d_1.count)
  };
}
function _temp4(value) {
  return pluginLabel(value);
}
function _temp5(value_0) {
  return pluginLabel(value_0);
}
function _temp6(value_1) {
  return pluginCategorizedLabel(value_1);
}
function _temp7(value_2) {
  return pluginCategorizedLabel(value_2);
}
function _temp8(value_3) {
  return pluginLabel(value_3);
}
function _temp9(value_4) {
  return pluginLabel(value_4);
}
function _temp10(department, departmentIndex) {
  return <Bar key={department.id} yAxisId="count" dataKey={department.id} name={department.name} fill={COLORS[departmentIndex % COLORS.length]} />;
}
function _temp11(value_5) {
  return pluginCategorizedLabel(value_5);
}
function _temp12(value_6) {
  return pluginCategorizedLabel(value_6);
}
function _temp13(department_0, departmentIndex_0) {
  return <Bar key={department_0.id} yAxisId="count" dataKey={department_0.id} name={department_0.name} fill={COLORS[departmentIndex_0 % COLORS.length]} />;
}